import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import useTranslations from '../../hooks/useTranslations';
import contacts from '../../config/contacts';

/* ═══════════════════════════════════════════
   DESIGN TOKENS — same as reference footer
   ═══════════════════════════════════════════ */
const C = {
  navy: '#0d1e2e',
  gold: '#b8975a',
  white: '#ffffff',
};

/* ── Social icons (Instagram, Facebook, X, TikTok) ── */
const IconInstagram = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
    <circle cx="12" cy="12" r="4.2" />
    <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);
const IconFacebook = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.5 21v-7h2.35l.38-2.85H13.5V9.3c0-.82.26-1.42 1.48-1.42h1.44V5.34c-.63-.07-1.37-.14-2.1-.14-2.08 0-3.52 1.27-3.52 3.6v2.05H8.4V14h2.4v7h2.7z" />
  </svg>
);
const IconX = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
  </svg>
);
const IconTikTok = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3c-1.88 0-3.52-1.11-4.34-1.48z" />
  </svg>
);

const HomeFooter = () => {
  const { availableLanguages, addLanguagePrefix } = useLanguage();
  const lang = availableLanguages.includes(window.location.pathname.split('/').filter(Boolean)[0])
    ? window.location.pathname.split('/').filter(Boolean)[0]
    : 'en';

  const { t } = useTranslations('footer', lang);

  const currentYear = new Date().getFullYear();

  /* ── Navigation links ── */
  const pathwaysLinks = [
    { path: '/visa-pathways/live-retire-abroad', label: t('pathways.retire', 'Golden Visa') },
    { path: '/visa-pathways/live-retire-abroad', label: t('pathways.retirement', 'Retirement / Elective') },
    { path: '/visa-pathways/work-remotely', label: t('pathways.nomad', 'Digital Nomad') },
    { path: '/visa-pathways/join-family', label: t('pathways.family', 'Family Reunification') },
    { path: '/visa-pathways/study-abroad', label: t('pathways.study', 'Student Visa') },
  ];

  const destinationsLinks = [
    { path: '/country/italy', label: 'Italy' },
    { path: '/country/greece', label: 'Greece' },
    { path: '/country/portugal', label: 'Portugal' },
    { path: '/country/spain', label: 'Spain' },
    { path: '/country/france', label: 'France' },
    { path: '/country/malta', label: 'Malta' },
  ];

  const companyLinks = [
    { path: '/about-us', label: t('company.about', 'About Us') },
    { path: '/about-us', label: t('company.team', 'Our Team') },
    { path: '/insights', label: t('company.insights', 'Insights & Blog') },
    { path: '/fees-scope', label: t('company.fees', 'Fees & Scope') },
    { path: '/contact', label: t('company.contact', 'Contact') },
  ];

  return (
    <footer className="dv-site-footer" style={{
      background: C.navy,
      borderTop: '1px solid rgba(255,255,255,0.05)',
      padding: '5.5rem 3rem 2.8rem',
      fontFamily: "'Jost', sans-serif",
    }}>
      <style>{`
        .ft-link {
          position: relative;
          transition: color 0.25s, padding-left 0.25s;
        }
        .ft-link:hover {
          padding-left: 8px;
          color: ${C.gold} !important;
        }
        .ft-platform-badge {
          transition: border-color 0.3s, background 0.3s;
        }
        .ft-platform-badge:hover {
          border-color: ${C.gold};
          background: rgba(184,151,90,0.12);
        }
        @media (max-width: 900px) {
          .ft-top { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .ft-top { grid-template-columns: 1fr !important; }
          .ft-bottom { flex-direction: column; text-align: center; }
          .dv-site-footer { padding: 3.5rem 1.5rem 1.5rem !important; }
        }
      `}</style>

        {/* ── TOP: 4-column grid ── */}
        <div className="ft-top" style={{
          display: 'grid',
          gridTemplateColumns: '1.8fr 1fr 1fr 1fr',
          gap: '3rem',
          marginBottom: '4rem',
        }}>
          {/* Column 1 — Brand */}
          <div>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 400,
              fontSize: '1.8rem',
              color: C.white,
              letterSpacing: '0.06em',
              marginBottom: '0.2rem',
            }}>
              DELVANTE
            </div>
            <div style={{
              fontSize: '0.58rem',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.28)',
              marginBottom: '1.3rem',
            }}>
              {t('brandSub', 'Immigration Advisory')}
            </div>
            <p style={{
              fontSize: '0.92rem',
              fontWeight: 300,
              color: 'rgba(255,255,255,0.36)',
              lineHeight: 1.8,
              maxWidth: 280,
              margin: 0,
            }}>
              {t('brandDesc', 'Expert EU immigration services for individuals and families. Transparent, personal, trusted — from consultation to residency.')}
            </p>
            <Link
              to={addLanguagePrefix('/login')}
              className="ft-platform-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.6rem',
                border: '1px solid rgba(184,151,90,0.22)',
                padding: '0.55rem 1.1rem',
                marginTop: '1.6rem',
                textDecoration: 'none',
              }}
            >
              <svg width="13" height="13" fill="none" stroke={C.gold} strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              <span style={{
                fontSize: '0.7rem',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: C.gold,
              }}>
                {t('platformBadge', 'Members Platform — Login')}
              </span>
            </Link>

            {/* ── Social icons ── (doar conturile cu URL setat in contacts.js) */}
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.8rem' }}>
              {[
                { key: 'instagram', label: 'Instagram', Icon: IconInstagram },
                { key: 'facebook', label: 'Facebook', Icon: IconFacebook },
                { key: 'x', label: 'X (Twitter)', Icon: IconX },
                { key: 'tiktok', label: 'TikTok', Icon: IconTikTok },
              ]
                .filter(({ key }) => Boolean(contacts.social?.[key]?.url))
                .map(({ key, label, Icon }) => {
                const url = contacts.social?.[key]?.url || '';
                const base = {
                  width: 38, height: 38, borderRadius: '50%',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: 'rgba(255,255,255,0.55)',
                  background: 'rgba(255,255,255,0.03)',
                  textDecoration: 'none',
                  transition: 'all 0.3s',
                };
                const hover = { color: C.gold, borderColor: C.gold, background: 'rgba(184,151,90,0.14)' };
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    style={base}
                    onMouseEnter={e => Object.assign(e.currentTarget.style, hover)}
                    onMouseLeave={e => Object.assign(e.currentTarget.style, base)}
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Column 2 — Pathways */}
          <div>
            <h5 style={{
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: '1.3rem',
              fontWeight: 500,
            }}>
              {t('colPathways', 'Pathways')}
            </h5>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {pathwaysLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={addLanguagePrefix(link.path)}
                    className="ft-link"
                    style={{
                      fontSize: '0.92rem',
                      fontWeight: 300,
                      color: 'rgba(255,255,255,0.36)',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Destinations */}
          <div>
            <h5 style={{
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: '1.3rem',
              fontWeight: 500,
            }}>
              {t('colDestinations', 'Destinations')}
            </h5>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {destinationsLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={addLanguagePrefix(link.path)}
                    className="ft-link"
                    style={{
                      fontSize: '0.92rem',
                      fontWeight: 300,
                      color: 'rgba(255,255,255,0.36)',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Company */}
          <div>
            <h5 style={{
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: '1.3rem',
              fontWeight: 500,
            }}>
              {t('colCompany', 'Company')}
            </h5>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {companyLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={addLanguagePrefix(link.path)}
                    className="ft-link"
                    style={{
                      fontSize: '0.92rem',
                      fontWeight: 300,
                      color: 'rgba(255,255,255,0.36)',
                      textDecoration: 'none',
                      display: 'inline-block',
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── BOTTOM ── */}
        <div className="ft-bottom" style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          paddingTop: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
        }}>
          <span style={{
            fontSize: '0.8rem',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.24)',
          }}>
            &copy; {currentYear} Delvante Global. {t('allRightsReserved', 'All rights reserved.')}
          </span>
          <span style={{
            fontSize: '0.8rem',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.24)',
          }}>
            <Link to={addLanguagePrefix('/privacy-policy')} style={{ color: 'rgba(255,255,255,0.24)', textDecoration: 'none', transition: 'color 0.25s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.24)'}>
              {t('privacy', 'Privacy Policy')}
            </Link>
            &nbsp;&middot;&nbsp;
            <Link to={addLanguagePrefix('/terms')} style={{ color: 'rgba(255,255,255,0.24)', textDecoration: 'none', transition: 'color 0.25s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.24)'}>
              {t('terms', 'Terms of Service')}
            </Link>
            &nbsp;&middot;&nbsp;
            <Link to={addLanguagePrefix('/privacy-policy')} style={{ color: 'rgba(255,255,255,0.24)', textDecoration: 'none', transition: 'color 0.25s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.65)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.24)'}>
              {t('cookies', 'Cookie Policy')}
            </Link>
          </span>
        </div>
    </footer>
  );
};

export default HomeFooter;
